import React from 'react';
import { Icon } from '@iconify/react';
import { Tooltip } from '@nextui-org/react';
import { AppLink, type AppLinkTarget } from './AppLink';

export interface NavSidebarItem {
  key: string;
  path: string;
  label: string;
  icon: string;
  target?: AppLinkTarget;
}

export interface NavSidebarGroup {
  heading: string | null;
  items: NavSidebarItem[];
}

export interface NavSidebarProps {
  /** Navigation groups (heading + items) */
  navGroups: NavSidebarGroup[];
  /** Current active section key (e.g. 'profile', 'behavior') */
  currentSection: string;
  /** Collapsed state: when true only icons are shown */
  isCollapsed: boolean;
  /** Toggle collapse */
  onToggleCollapse: () => void;
  /** Header title shown when not collapsed (e.g. 'Settings') */
  headerTitle: string;
  /** Returns true if the nav item with this key is disabled */
  isDisabled?: (key: string) => boolean;
  /** Optional class for the aside root */
  className?: string;
  /** Tooltip when collapsed (e.g. "Expand menu") */
  expandMenuLabel?: string;
  /** Tooltip when expanded (e.g. "Collapse menu") */
  collapseMenuLabel?: string;
  /** When set, internal nav items emit this instead of navigating; parent can run custom logic then navigate */
  onInternalNavigate?: (path: string, item: NavSidebarItem) => void;
  navClassName?: string;
}

/**
 * Sidebar (aside) with collapsible nav. Uses AppLink: external = new tab, internal/undefined = in-app navigation.
 */
export function NavSidebar(props: NavSidebarProps) {
  const {
    navGroups,
    currentSection,
    isCollapsed,
    onToggleCollapse,
    headerTitle,
    isDisabled = () => false,
    className = '',
    navClassName = '',
    expandMenuLabel = 'Expand menu',
    collapseMenuLabel = 'Collapse menu',
    onInternalNavigate,
  } = props;

  const asideClass = `nav-sidebar flex-shrink-0 flex flex-col border-r border-default-200 bg-content1 transition-[width] duration-200 ease-in-out ${isCollapsed ? 'w-[72px]' : 'w-[220px]'} ${className}`.trim();

  return (
    <aside className={asideClass} style={{ fontFamily: 'Poppins, sans-serif' }}>
      <div className="flex items-center gap-2 border-b border-default-200 min-h-[48px]">
        {!isCollapsed && (
          <span className="font-semibold text-foreground px-4 flex-1 truncate" style={{ fontSize: '14px' }}>
            {headerTitle}
          </span>
        )}
        <Tooltip content={isCollapsed ? expandMenuLabel : collapseMenuLabel} placement="right">
          <button
            type="button"
            onClick={onToggleCollapse}
            className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-md text-default-500 hover:bg-[#F2F4F7] hover:text-[#101828] transition-colors mx-auto"
            aria-label={isCollapsed ? expandMenuLabel : collapseMenuLabel}
          >
            <Icon icon={isCollapsed ? 'mdi:chevron-right' : 'mdi:chevron-left'} width={20} height={20} />
          </button>
        </Tooltip>
      </div>
      <nav className={`flex-1 overflow-y-auto py-2 ${navClassName ? navClassName : ''}`} style={{ scrollbarWidth: 'thin', scrollbarColor: '#E5E7EB transparent' }}>
        {navGroups.map((group) => (
          <div key={group.heading} className="mb-3">
            {!isCollapsed && group.heading && (
              <div className="px-4 py-1 font-bold text-default-500" style={{ fontSize: '11px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {group.heading}
              </div>
            )}
            {group.items.map((item) => {
              const active = currentSection.toLowerCase() === item.key.toLowerCase();
              const disabled = isDisabled(item.key);
              const target = item.target ?? 'internal';
              const linkClass = `
                w-full flex items-center text-left transition-colors border-l-2
                ${isCollapsed ? 'justify-center px-0 py-2 min-h-[36px]' : 'gap-2.5 px-4 py-2'}
                ${active
                  ? 'bg-[#F2F4F7] text-foreground border-l-[#2563EB]'
                  : 'text-default-600 hover:bg-[#F2F4F7] border-l-transparent'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `.replace(/\s+/g, ' ').trim();
              const content = (
                <>
                  {isCollapsed ? (
                    <Tooltip content={item.label} placement="right">
                      <span className="flex-shrink-0">
                        <Icon icon={item.icon} width={16} height={16} className={active ? 'text-[#2563EB]' : 'text-default-500'} />
                      </span>
                    </Tooltip>
                  ) : (
                    <Icon icon={item.icon} width={16} height={16} className={`flex-shrink-0 ${active ? 'text-[#2563EB]' : 'text-default-500'}`} />
                  )}
                  {!isCollapsed && <span style={{ fontSize: '14px', fontWeight: active ? 500 : 400, lineHeight: '20px' }}>{item.label}</span>}
                </>
              );
              const linkEl = (
                <AppLink
                  to={item.path}
                  target={target}
                  className={linkClass}
                  style={active ? { backgroundColor: '#F2F4F7', borderLeftColor: '#2563EB' } : undefined}
                  disabled={disabled}
                  aria-label={item.label}
                  onInternalClick={
                    target === 'internal' && onInternalNavigate
                      ? (path) => onInternalNavigate(path, item)
                      : undefined
                  }
                >
                  {content}
                </AppLink>
              );
              return isCollapsed ? (
                <Tooltip key={item.key} content={item.label} placement="right">
                  {linkEl}
                </Tooltip>
              ) : (
                <div key={item.key}>{linkEl}</div>
              );
            })}
          </div>
        ))}
      </nav>
    </aside>
  );
}

export default NavSidebar;
